/**
 * AttentionWidget — a backlog strip that answers "what do I have to act on?".
 *
 * Built for a LANDING page, where a wall of KPI cards reading `0` is noise: it
 * costs a screen of scrolling to say nothing is wrong. Here a zero is quiet and
 * a non-zero is loud, so the page is scanned in one glance and stays calm when
 * there is nothing to do. When every count is zero the tiles collapse into a
 * single "all clear" line.
 *
 * Config-driven and module-agnostic — a preset supplies `items`:
 *   [{ metric, label?, tone }]   tone: 'critical' | 'warning' | 'info'
 *
 * Every item MUST be a point-in-time metric (registry `date_column=None`).
 * A backlog filtered to "last 30 days" is a different, misleading number —
 * "3 overdue CAPAs" must mean three, not three raised recently.
 *
 * No new API surface: values come from the same RBAC-checked
 * /api/dashboard/v2/kpis call every KPI widget uses, and a tile drills through
 * the shared DrilldownModal, so record-level access stays server-enforced.
 */
import React, { useState } from 'react';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useKpis } from '../hooks/useDashboardData';
import DrilldownModal from './DrilldownModal';
import { CARD, LABEL } from '../ui';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';

// Tone drives the ALERT state only. An item at zero is always neutral grey —
// colour on this strip means "look at me", so spending it on a zero would
// train the reader to ignore it.
const TONE = {
  critical: { value: 'text-red-600', dot: 'bg-red-500', ring: 'hover:border-red-300' },
  warning: { value: 'text-amber-600', dot: 'bg-amber-500', ring: 'hover:border-amber-300' },
  info: { value: 'text-blue-600', dot: 'bg-blue-500', ring: 'hover:border-blue-300' },
};

function Tile({ item, metric, loading, onClick }) {
  const tone = TONE[item.tone] || TONE.info;
  const value = metric?.value;
  const label = item.label || metric?.label || item.metric;
  // null/undefined (metric errored or unauthorized) is NOT zero — it must not
  // read as "nothing to do here".
  const unknown = value === null || value === undefined;
  const alerting = !unknown && Number(value) > 0;
  const clickable = alerting && typeof onClick === 'function' && metric?.drillable !== false;

  if (loading) {
    return (
      <div className={`${CARD} px-3.5 py-3`}>
        <div className="h-6 w-10 animate-pulse rounded bg-gray-100" />
        <div className="mt-2 h-3 w-4/5 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={`${CARD} group w-full px-3.5 py-3 text-left transition-all duration-150
                  ${clickable ? `cursor-pointer hover:shadow-md ${tone.ring}
                     focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500` : ''}`}
    >
      <div className="flex items-baseline gap-2">
        {alerting && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} />}
        <span className={`text-2xl font-semibold leading-none tabular-nums
                          ${unknown ? 'text-gray-300' : alerting ? tone.value : 'text-gray-400'}`}>
          {unknown ? '—' : value}
        </span>
        {clickable && (
          <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
        )}
      </div>
      <div className={`mt-1.5 truncate text-xs font-medium
                       ${alerting ? 'text-gray-700' : 'text-gray-400'}`} title={label}>
        {label}
      </div>
    </Tag>
  );
}

export default function AttentionWidget({ items = [], title = 'Needs attention' }) {
  const {
    module, siteId, departmentId, dimensions, fyStartMonth, standard, refreshMs,
  } = useDashboard();

  const keys = items.map((i) => i.metric);
  // `time` is deliberately NOT passed: these are point-in-time backlogs (see
  // the file header). `compare` is off too — a backlog has no previous period.
  const { metrics: values, loading, unauthorized, error } = useKpis({
    module, metrics: keys, siteId, departmentId, dimensions, fyStartMonth, standard, refreshMs,
  });

  const [drilled, setDrilled] = useState(null);

  const unauthSet = new Set(unauthorized);
  // A metric the user may not read is dropped, not shown as a dash — an empty
  // tile would still leak that the metric exists.
  const shown = items.filter((i) => !unauthSet.has(i.metric));
  const total = shown.reduce((n, i) => {
    const v = values[i.metric]?.value;
    return n + (v === null || v === undefined ? 0 : Number(v) || 0);
  }, 0);
  const anyKnown = shown.some((i) => {
    const v = values[i.metric]?.value;
    return v !== null && v !== undefined;
  });
  const allClear = !loading && shown.length > 0 && anyKnown && total === 0;

  if (shown.length === 0) return null;

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className={LABEL}>{title}</span>
        <span className="h-px flex-1 bg-gray-100" />
      </div>

      {isSiteRequiredError(error) ? (
        // A dash per tile would read as "nothing to act on", which is the one
        // thing a backlog strip must never say when it does not know.
        <SiteRequiredNotice compact />
      ) : allClear ? (
        <div className={`${CARD} flex items-center gap-2.5 px-4 py-3.5`}>
          <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
          <span className="text-sm font-medium text-gray-700">
            Nothing overdue or awaiting approval.
          </span>
          <span className="hidden text-xs text-gray-400 sm:inline">
            No open backlog across incidents, CAPAs or high-risk hazards.
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {shown.map((item) => (
            <Tile
              key={item.metric}
              item={item}
              metric={values[item.metric]}
              loading={loading && !values[item.metric]}
              onClick={() => setDrilled({
                key: item.metric,
                label: item.label || values[item.metric]?.label || item.metric,
              })}
            />
          ))}
        </div>
      )}

      <DrilldownModal
        open={!!drilled}
        metric={drilled?.key}
        title={drilled?.label}
        onClose={() => setDrilled(null)}
      />
    </div>
  );
}
